import { AriaDescriber } from '@angular/cdk/a11y';
import {
  ConnectionPositionPair,
  FlexibleConnectedPositionStrategy,
  Overlay,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  ComponentRef,
  Directive,
  ElementRef,
  inject,
  input,
  linkedSignal,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { TooltipPanelComponent } from '@shared/components/tooltip/tooltip-panel.component';
import { TooltipPosition } from '@shared/models';

@Directive({
  selector: '[b2cTooltip]',
  host: {
    '(mouseenter)': 'onMouseEnter()',
    '(mouseleave)': 'onMouseLeave()',
    '(focusin)': 'onFocusIn()',
    '(focusout)': 'onFocusOut()',
    '(keydown.escape)': 'onEsc()',
  },
})
export class TooltipDirective implements OnDestroy {
  readonly b2cTooltipDisabled = input(false);

  // Inputs
  b2cTooltipShowDelay = input<number>(120);
  b2cTooltipHideDelay = input<number>(100);
  b2cTooltipPosition = input<TooltipPosition>('above');
  content = input.required<string | TemplateRef<unknown>>({
    alias: 'b2cTooltip',
  });

  isTooltipDisabled = linkedSignal(() => this.b2cTooltipDisabled());

  private lastTouchAt = 0;
  private attached = false;
  private overlayRef?: OverlayRef;
  private showTid: NodeJS.Timeout;
  private hideTid: NodeJS.Timeout;
  private currentPosition: TooltipPosition = 'above';
  private compRef: ComponentRef<TooltipPanelComponent>;
  private positionStrategySubscription: Subscription;
  private positionStrategy: FlexibleConnectedPositionStrategy;

  private readonly overlay: Overlay = inject(Overlay);
  private readonly aria: AriaDescriber = inject(AriaDescriber);
  private readonly vcr: ViewContainerRef = inject(ViewContainerRef);
  private readonly hostRef: ElementRef<HTMLElement> = inject(ElementRef);

  // Desktop hover/focus
  onMouseEnter() {
    if (Date.now() - this.lastTouchAt < 600) return; // ignore ghost mouse events after touch
    this.queueShow();
  }
  onMouseLeave() {
    this.queueHide();
  }
  onFocusIn() {
    this.queueShow();
  }
  onFocusOut() {
    this.queueHide(true);
  }
  onEsc() {
    this.hide(0);
  }

  ngOnDestroy() {
    clearTimeout(this.showTid);
    clearTimeout(this.hideTid);
    this.detach();
    this.overlayRef?.dispose();
    this.positionStrategySubscription?.unsubscribe();
  }

  private queueShow() {
    if (this.isTooltipDisabled() || !this.content()) return;
    clearTimeout(this.hideTid);
    clearTimeout(this.showTid);
    this.showTid = setTimeout(() => this.show(), this.b2cTooltipShowDelay());
  }

  private queueHide(immediate = false) {
    clearTimeout(this.showTid);
    clearTimeout(this.hideTid);
    this.hideTid = setTimeout(
      () => this.hide(),
      immediate ? 0 : this.b2cTooltipHideDelay()
    );
  }

  private show() {
    if (this.attached || this.isTooltipDisabled() || !this.content()) return;

    if (!this.overlayRef) this.overlayRef = this.createOverlay();
    const ref = this.overlayRef;

    // Always use the panel component (it can render string or template)
    const portal = new ComponentPortal(TooltipPanelComponent, this.vcr);
    const compRef = ref.attach(portal);
    this.compRef = compRef;

    if (this.content() instanceof TemplateRef) {
      compRef.setInput('tpl', this.content() as TemplateRef<unknown>);
      compRef.setInput('text', '');
    } else {
      const text = String(this.content() ?? '');
      compRef.setInput('text', text);
      this.aria.describe(this.hostRef.nativeElement, text);
    }

    compRef.setInput('position', this.currentPosition);

    queueMicrotask(() =>
      compRef.location.nativeElement.classList.add('visible')
    );

    this.attached = true;
  }

  private hide(delay = this.b2cTooltipHideDelay()) {
    clearTimeout(this.hideTid);
    this.hideTid = setTimeout(() => this.detach(), delay);
  }

  private detach() {
    if (!this.overlayRef || !this.overlayRef.hasAttached()) return;
    this.compRef.location.nativeElement.classList.remove('visible');
    setTimeout(() => {
      this.overlayRef?.detach();
      this.attached = false;
    }, 120); // wait fade-out
    const text = this.compRef.instance.text();
    if (this.compRef && typeof text === 'string' && text) {
      this.aria.removeDescription(this.hostRef.nativeElement, text);
    }
  }

  private createOverlay(): OverlayRef {
    this.positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.hostRef)
      .withPositions(this.buildPositions(this.b2cTooltipPosition()))
      .withPush(true);

    // Watch for flips to update arrow direction
    this.positionStrategySubscription =
      this.positionStrategy.positionChanges.subscribe(({ connectionPair }) => {
        this.currentPosition = this.mapToPosition(connectionPair);
        if (this.compRef) {
          this.compRef.setInput('position', this.currentPosition);
          this.compRef.changeDetectorRef.markForCheck();
        }
      });

    return this.overlay.create({
      positionStrategy: this.positionStrategy,
      panelClass: 'app-tooltip-panel',
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });
  }

  private buildPositions(pref: TooltipPosition): ConnectionPositionPair[] {
    const p = {
      above: new ConnectionPositionPair(
        { originX: 'center', originY: 'top' },
        { overlayX: 'center', overlayY: 'bottom' },
        0,
        -8 // offsetY up
      ),
      below: new ConnectionPositionPair(
        { originX: 'center', originY: 'bottom' },
        { overlayX: 'center', overlayY: 'top' },
        0,
        8
      ),
      left: new ConnectionPositionPair(
        { originX: 'start', originY: 'center' },
        { overlayX: 'end', overlayY: 'center' },
        -8,
        0
      ),
      right: new ConnectionPositionPair(
        { originX: 'end', originY: 'center' },
        { overlayX: 'start', overlayY: 'center' },
        8,
        0
      ),
    };
    const order: Record<TooltipPosition, ConnectionPositionPair[]> = {
      above: [p.above, p.below, p.right, p.left],
      below: [p.below, p.above, p.right, p.left],
      left: [p.left, p.right, p.above, p.below],
      right: [p.right, p.left, p.above, p.below],
    };
    return order[pref];
  }

  private mapToPosition(pair: ConnectionPositionPair): TooltipPosition {
    const { originX, originY, overlayX, overlayY } = pair;
    if (originY === 'top' && overlayY === 'bottom') return 'above';
    if (originY === 'bottom' && overlayY === 'top') return 'below';
    if (originX === 'start' && overlayX === 'end') return 'left';
    return 'right';
  }
}
